import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button onClick={scrollToTop} className="flex items-center gap-3 group">
              <img 
                src="https://static.readdy.ai/image/90e3b7d98f6a66a16793c7597f1aaf38/255eb9fd06a7d8f6ca0adc21239c3ba7.png" 
                alt="BRAX Facility Group"
                className="h-12 w-auto object-contain rounded-lg"
              />
            </button>

            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('hero')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.home')}
              </button>
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.services')}
              </button>
              <button onClick={() => scrollToSection('environments')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.environments')}
              </button>
              <button onClick={() => scrollToSection('projects')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.serviceAreas')}
              </button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.about')}
              </button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                {t('nav.contact')}
              </button>
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${i18n.language === 'en' ? 'border-[#3B82F6]' : 'border-transparent opacity-70'}`}
                  title="English"
                >
                  <img src="https://flagcdn.com/w40/us.png" alt="English" className="w-full h-full object-cover" />
                </button>
                <button
                  onClick={() => changeLanguage('es')}
                  className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${i18n.language === 'es' ? 'border-[#3B82F6]' : 'border-transparent opacity-70'}`}
                  title="Español"
                >
                  <img src="https://flagcdn.com/w40/es.png" alt="Español" className="w-full h-full object-cover" />
                </button>
              </div>
              <a href="mailto:contact@braxfacility.com" className={`flex items-center gap-2 text-sm font-medium transition-colors ${isScrolled ? 'text-[#0F172A]' : 'text-white'}`}>
                <i className="ri-mail-line"></i>
                contact@braxfacility.com
              </a>
              <button onClick={() => scrollToSection('contact')} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors px-6 py-2 bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] text-white shadow">
                {t('nav.getQuote')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          <div className="fixed top-20 left-0 right-0 bg-white shadow-lg z-50 lg:hidden">
            <div className="px-6 py-6 space-y-4">
              {/* Navigation Links */}
              <button
                onClick={() => {
                  scrollToSection('services');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-900 hover:text-[#3B82F6] transition-colors font-medium"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => {
                  scrollToSection('environments');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-900 hover:text-[#3B82F6] transition-colors font-medium"
              >
                {t('nav.environments')}
              </button>
              <button
                onClick={() => {
                  scrollToSection('projects');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-900 hover:text-[#3B82F6] transition-colors font-medium"
              >
                {t('nav.serviceAreas')}
              </button>
              <button
                onClick={() => {
                  scrollToSection('about');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-900 hover:text-[#3B82F6] transition-colors font-medium"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => {
                  scrollToSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-900 hover:text-[#3B82F6] transition-colors font-medium"
              >
                {t('nav.contact')}
              </button>

              {/* Language Selector */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-3">{t('nav.language')}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      i18n.language === 'en'
                        ? 'border-[#3B82F6] bg-[#3B82F6]/10'
                        : 'border-gray-200 hover:border-[#3B82F6]'
                    }`}
                  >
                    <span className="text-2xl">🇺🇸</span>
                    <span className="block text-sm font-medium text-gray-900 mt-1">English</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('es')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                      i18n.language === 'es'
                        ? 'border-[#3B82F6] bg-[#3B82F6]/10'
                        : 'border-gray-200 hover:border-[#3B82F6]'
                    }`}
                  >
                    <span className="text-2xl">🇪🇸</span>
                    <span className="block text-sm font-medium text-gray-900 mt-1">Español</span>
                  </button>
                </div>
              </div>

              {/* Phone */}
              <a
                href="mailto:contact@braxfacility.com"
                className="flex items-center gap-3 py-3 text-gray-900 hover:text-[#3B82F6] transition-colors"
              >
                <i className="ri-mail-line text-xl"></i>
                <span className="font-medium">contact@braxfacility.com</span>
              </a>

              {/* CTA Button */}
              <button
                onClick={() => {
                  scrollToSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] text-white rounded-lg font-medium hover:from-[#2563EB] hover:to-[#3B82F6] transition-colors whitespace-nowrap"
              >
                {t('nav.getQuote')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
